import { afterNextRender, ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { MotionPreferenceService } from '../../../../services/motion-preference.service';
import { NgIcon } from '@ng-icons/core';

/** Types visuels disponibles pour un toast */
export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'neutral';

/** Action optionnelle affichée sous forme de bouton dans le toast */
export interface ToastAction {
  /** Libellé du bouton d'action */
  label: string;
  /** Callback exécuté au clic (le toast se ferme ensuite automatiquement) */
  onClick: () => void;
}

/**
 * Configuration d'un toast.
 *
 * Gestion de l'auto-dismiss (appliquée par `NotificationsService.show`) :
 * - Une `duration` explicite (y compris `0`) est toujours respectée : `0` = toast
 *   persistant, toute valeur > 0 = fermeture automatique après ce délai en ms.
 * - Si `duration` est absente ET qu'une `action` est fournie, le toast est considéré
 *   comme persistant : on évite de fermer un toast avant que l'utilisateur ait eu la
 *   possibilité de cliquer sur son action.
 * - Si `duration` est absente et qu'il n'y a pas d'`action`, la durée historique du
 *   type s'applique (3000ms pour success/warning/info/neutral, 5000ms pour error).
 */
export interface ToastConfig {
  /** Identifiant unique — généré automatiquement (crypto.randomUUID()) si absent */
  id?: string;
  /** Type visuel du toast (défaut: 'neutral') */
  type?: ToastType;
  /** Message affiché */
  message: string;
  /** true/absent = icône par défaut du type, false = pas d'icône, string = nom d'icône iconoir custom */
  icon?: boolean | string;
  /** Durée d'affichage en ms avant fermeture automatique. 0 = persistant (voir doc ci-dessus) */
  duration?: number;
  /** Action optionnelle (label + callback) affichée sous forme de bouton */
  action?: ToastAction;
  /** Affiche le bouton de fermeture (X). Défaut: true */
  dismissible?: boolean;
}

/** Icônes iconoir par défaut, une par type de toast */
const DEFAULT_ICONS: Record<ToastType, string> = {
  success: 'iconoirCheckCircle',
  error: 'iconoirXmarkCircle',
  warning: 'iconoirWarningTriangle',
  info: 'iconoirInfoCircle',
  neutral: 'iconoirBell',
};

/**
 * Rendu d'un toast unique — composant presentational.
 * Le cycle de vie (auto-dismiss, id, empilement) est géré par NotificationsService
 * et ToastContainerComponent ; ce composant se contente d'afficher le contenu et
 * de jouer les animations d'entrée/sortie.
 */
@Component({
  selector: 'app-toast',
  imports: [NgIcon],
  templateUrl: './toast.component.html',
  styleUrl: './toast.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastComponent {
  /** Configuration du toast à afficher */
  toast = input.required<ToastConfig>();
  /** true pendant l'animation de sortie — piloté par le container via NotificationsService */
  closing = input(false);

  /** Émis quand le survol ou le focus commence : le décompte de fermeture se suspend. */
  paused = output<void>();
  /** Émis quand le survol et le focus sont terminés : le décompte reprend. */
  resumed = output<void>();

  /** Émis quand l'utilisateur ferme le toast (bouton X ou clic sur l'action) */
  closed = output<void>();

  /**
   * Animation « goutte » (même langage que l'annulation de suppression et la
   * transition de thème) : lue une fois à la création, le toast ne change pas
   * de mode en cours de vie. Sinon : simple fondu.
   */
  protected readonly richMotion = inject(MotionPreferenceService).richMotion();

  #entering = signal(true);
  /** true juste après insertion, le temps de laisser le navigateur peindre l'état initial avant la transition CSS */
  protected readonly entering = this.#entering.asReadonly();

  protected readonly type = computed<ToastType>(() => this.toast().type ?? 'neutral');
  protected readonly isDismissible = computed(() => this.toast().dismissible ?? true);
  /** Une erreur interrompt la lecture d'écran (alert) ; le reste attend la fin de la phrase (status). */
  protected readonly role = computed(() => (this.type() === 'error' ? 'alert' : 'status'));
  protected readonly action = computed(() => this.toast().action ?? null);

  protected readonly iconName = computed<string | null>(() => {
    const icon = this.toast().icon;
    if (icon === false) return null;
    if (typeof icon === 'string') return icon;
    return DEFAULT_ICONS[this.type()];
  });

  constructor() {
    afterNextRender(() => {
      // Force le rendu dans l'état "entering" avant de basculer, pour que la transition CSS s'anime.
      requestAnimationFrame(() => this.#entering.set(false));
    });
  }

  #hovered = false;
  #focused = false;

  protected onHover(hovered: boolean): void {
    this.#setInteracting(hovered, this.#focused);
  }

  protected onFocus(event: FocusEvent, focused: boolean): void {
    // Le focus qui passe d'un bouton du toast à l'autre ne doit pas relancer le décompte.
    const next = event.relatedTarget as Node | null;
    if (!focused && next && (event.currentTarget as HTMLElement).contains(next)) return;
    this.#setInteracting(this.#hovered, focused);
  }

  #setInteracting(hovered: boolean, focused: boolean): void {
    const was = this.#hovered || this.#focused;
    this.#hovered = hovered;
    this.#focused = focused;
    const now = hovered || focused;
    if (now && !was) this.paused.emit();
    else if (!now && was) this.resumed.emit();
  }

  protected onClose(): void {
    this.closed.emit();
  }

  protected onAction(): void {
    this.action()?.onClick();
    this.closed.emit();
  }
}
